import z from "zod";
import { RecurrenceSchema, stripNulls } from "./Recurrence";

// Wrapped in `stripNulls` so a null recurrence/recurrencePattern (or any null field the backend
// serialises for an absent value) is treated as absent rather than failing validation and
// white-screening the whole booking list. See Recurrence.ts / createIngestor.
export const AmenityBookingSchema = z.preprocess(
  stripNulls,
  z.object({
    id: z.string(),
    originalBookingId: z.string(),
    locationId: z.string().optional(), // Optional: can be derived from parent booking
    roomId: z.string().optional(), // Optional: can be derived from parent booking
    memberId: z.string().optional(), // Optional: can be derived from parent booking
    // Assignee display name, enriched server-side (GET /amenity-bookings) so the calendar can show
    // WHO an amenity is booked by, matching room bookings.
    memberName: z.string().optional(),
    amenityId: z.string(),
    description: z.string().optional(),
    recurrence: RecurrenceSchema.optional(),
    recurrencePattern: RecurrenceSchema.optional(), // Stores original recurrence for expanded bookings
    date: z.coerce.date().optional(), // Optional: can be derived from parent booking
    startTime: z.string().optional(), // Optional: can be derived from parent booking
    endTime: z.string().optional(), // Optional: can be derived from parent booking
    // R23 — mirrors the parent booking's buffer state so the calendar renders the same
    // pre/post buffer stripes on amenity tiles. See hearth/docs/BOOKING_RULES.md#r23.
    // hasBuffers is the legacy combined flag (pre && post); hasPreBuffer/hasPostBuffer carry the
    // parent's directional state so a single-sided buffer renders correctly rather than all-or-none.
    hasBuffers: z.boolean().optional(),
    hasPreBuffer: z.boolean().optional(),
    hasPostBuffer: z.boolean().optional(),
    // R27 — per-occurrence amenity rows. `occurrenceDate` (YYYY-MM-DD) marks a single-date
    // override; `amenityExceptions` lists a series row's customised dates. Must exist here or
    // createIngestor strips them. See hearth/docs/BOOKING_RULES.md#r27.
    occurrenceDate: z.string().optional(),
    amenityExceptions: z.array(z.string()).optional(),
    createdAt: z.union([z.string(), z.coerce.string()]).optional(),
    updatedAt: z.union([z.string(), z.coerce.string()]).optional(),
  }),
);

export type AmenityBooking = z.infer<typeof AmenityBookingSchema>;
